import { useEffect, useState } from "react";
import {
  useNavigate,
  useParams,
  createFileRoute,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { resendOtpTask, signAndCompleteTask } from "../../api/completeTask";
import { getProcessTasks } from "../../api/processRequest";
import { notify, notifyWithTitle, ToastNotifyStyle } from "../../pages/public/components/Toast";
import FormRenderer, {
  FormRendererProps,
} from "../../pages/protected/components/FormRenderer";
import { getTaskForm } from "../../api/tasks";
import PasswordModal from "../../pages/protected/signature/components/password-modal";
import Loading from "../../components/Loading";
import ResendOtpModal from "../../pages/protected/signature/components/resend-otp-modal";
import extractInnerMessage from "../../utils/apiErrorUtils";

type Variables = Record<
  string,
  {
    type: string;
    value: string;
    valueInfo: object;
  }
>;

interface FormObject {
  form: [];
  variables: Variables;
}

export const Route = createFileRoute("/__auth/signature/$requestId/$taskId")({
  component: SignatureWrapper,
  pendingComponent: Loading,
});

function SignatureWrapper() {
  const match = Route.useMatch();
  return <Signature key={match.pathname} />;
}

function Signature(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [openOtp, setOpenOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [email, setEmail] = useState("");
  const [values, setValues] = useState({
    inputs: {},
    variabels: {},
  });
  const [reloadForm, setReloadForm] = useState<(() => void) | undefined>(undefined);
  const params = useParams({
    from: "/__auth/signature/$requestId/$taskId",
  }) as { requestId: string; taskId: string };
  const { data: formData, error } = useQuery({
    queryKey: ["task-form", params.requestId, params.taskId],
    queryFn: async () =>
      (await getTaskForm(
        params.requestId,
        params.taskId,
      )) as unknown as FormObject,
    retry: false,
  });

  useEffect(() => {
    if (error) {
      navigate({ to: "/error" });
    }
  }, [error, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const callback: FormRendererProps["callback"] = (data, _, __, reloadFormView) => {
    setValues(data as unknown as typeof values);
    // Mantém o formulário oculto enquanto o modal de assinatura está aberto.
    setReloadForm(() => reloadFormView);
    setOpen(true);
  };

  const callbackOtp: FormRendererProps["callbackOtp"] = (data) => {
    setValues(data as unknown as typeof values);
    setOpenOtp(true);
  };

  const sendForm = async (requestId: string, taskId: string) => {
    setLoading(true);
    const inputs = values.inputs;
    try {
      await signAndCompleteTask(inputs, requestId, taskId, otp);
      const task = await getProcessTasks(requestId);
      let pathTask = "task";

      if (task.data[0]) {
        if (task.data[0].isSignatureTask) {
          pathTask = "signature";
        }
        void navigate({
          to: `/${pathTask}/${requestId}/${task.data[0].id}` as any,
        });
      } else {
        void navigate({ to: `/` });
      }
      localStorage.removeItem(taskId || "form");
      notify(t("signature.signSuccess"), "success", "");
    } catch (error) {
      const erroString = extractInnerMessage(error);
      console.error("error sign:", erroString);
      notifyWithTitle(t("signature.signError"), erroString, "error", { fontSize: "16px"} as ToastNotifyStyle);

      reloadForm && reloadForm();
    } finally {
      setOpen(false);
      setLoading(false);
    }
  };

  const resendOtp = async (requestId: string, taskId: string) => {
    setLoading(true);
    const inputs = values.inputs;
    try {
      await resendOtpTask(inputs, requestId, taskId, email);

      notify(t("signature.otpResent"), "success", "");
    } catch (error) {
      const erroString = extractInnerMessage(error);
      console.error("error resend otp:", erroString);
      notifyWithTitle(t("signature.otpResendError"), erroString, "error", { fontSize: "16px"} as ToastNotifyStyle);
    } finally {
      setOpenOtp(false);
      setLoading(false);
    }
  };

  const cancel = () => {
    void navigate({ to: `/` });
  };

  const handleClose = () => {
    setOpen(false);
    reloadForm && reloadForm();
  };

  const handleCloseOtp = () => {
    setOpenOtp(false);
  };

  return (
    <>
      <FormRenderer
        callback={callback}
        callbackOtp={callbackOtp}
        cancel={cancel}
        formData={formData}
        processId={params.requestId}
        taskId={params.taskId}
        type="signature"
      />
      <PasswordModal
        handleClose={handleClose}
        isLoading={loading}
        open={open}
        password={otp}
        processId={params.requestId}
        sendForm={sendForm}
        setPassword={setOtp}
        taskId={params.taskId}
      />
      <ResendOtpModal
        handleClose={handleCloseOtp}
        isLoading={loading}
        open={openOtp}
        email={email}
        processId={params.requestId}
        sendForm={resendOtp}
        setEmail={setEmail}
        taskId={params.taskId}
      />
    </>
  );
}
